import { useState } from "react";
import { useForm } from "react-hook-form";
import type { FieldErrors } from "react-hook-form";
import { useDataFetch } from "../../../hooks/use-data-fetch";
import { useNavigate } from "react-router-dom";

// Define interface for login form data
export interface SignInFormData {
  email: string;
  password: string;
}

export const saveSession = (data: {
  token: string;
  name: string;
  country: string;
  multyhealth_code: string;
}) => {
  localStorage.setItem("token", data.token);
  localStorage.setItem("name", data.name);
  localStorage.setItem("country", data.country);
  localStorage.setItem("multyhealth_code", data.multyhealth_code);
};

export const useSignInForm = () => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [twoFactorRequired, setTwoFactorRequired] = useState<boolean>(false);
  const [twoFactorUserId, setTwoFactorUserId] = useState<string | null>(null);
  const [userName, setUserName] = useState<string>("");
  const [notConfirmed, setNotConfirmed] = useState<{
    email: string;
    emailSent: boolean;
    retryAfterSeconds: number;
    limitReached: boolean;
  } | null>(null);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<SignInFormData>({
    defaultValues: {
      email: "",
      password: ""
    }
  });

  const { postData } = useDataFetch();

  const { mutateAsync } = postData("/users/login", {});
  const { mutateAsync: googleLogin } = postData("/users/login/google", {});

  // Function to handle form submission
  const onSubmit = async (data: SignInFormData) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      setNotConfirmed(null);

      const loginData = {
        email: data.email,
        password: data.password
      };

      // Call the login API endpoint
      const result = await mutateAsync(loginData);
      
      // Credenciales correctas pero cuenta sin activar: el backend ya reenvio
      // el correo (si la espera lo permitia).
      if (result && result.message === "user_not_confirmed") {
        setNotConfirmed({
          email: result.data?.email ?? data.email,
          emailSent: !!result.data?.email_sent,
          retryAfterSeconds: result.data?.retry_after_seconds ?? 0,
          limitReached: !!result.data?.resend_limit_reached,
        });
        return result;
      }

      return handleLoginResult(result);
    } catch (error: any) {
      handleLoginError(error);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Login ya autenticado (correo o Google): 2FA si aplica, o guarda la sesión.
  const handleLoginResult = (result: any) => {
    if (result && result.message === "user_two_factor_required" && result.data?.two_factor_required) {
      setTwoFactorRequired(true);
      setTwoFactorUserId(result.data.user_id);
      setUserName(result.data.name);
      return result;
    }

    if (result && result.message === "token_create_success" && result.data?.token) {
      saveSession(result.data);
      navigate("/store", { replace: true });
      reset(); // Clear the form
      return result;
    }

    throw new Error(result?.message || "Error al iniciar sesión");
  };

  const handleLoginError = (error: any) => {
    const errorMessage = error?.response?.data?.message || error?.message;

    if (errorMessage === "user_search_by_credentials_not_match") {
      setSubmitError("Correo electrónico o contraseña incorrectos");
    } else if (errorMessage === "user_search_by_credentials_not_found") {
      setSubmitError("Por favor ingresa tu correo y contraseña");
    } else if (errorMessage === "user_search_by_google_invalid_token") {
      setSubmitError("No pudimos validar tu cuenta de Google. Intenta nuevamente.");
    } else if (errorMessage === "UNAUTHORIZED") {
      setSubmitError("Credenciales inválidas");
    } else {
      setSubmitError(errorMessage || "Error al iniciar sesión");
    }
  };

  // "Continuar con Google": si el correo no tiene cuenta se lleva al registro
  // con los datos de Google precargados.
  const loginWithGoogle = async (credential: string) => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      setNotConfirmed(null);

      const result = await googleLogin({ credential });

      if (result?.message === "user_google_not_registered") {
        navigate("/sign-up", { state: { google: { credential, ...result.data } } });
        return result;
      }

      return handleLoginResult(result);
    } catch (error: any) {
      handleLoginError(error);
      return null;
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetNotConfirmed = () => setNotConfirmed(null);

  const resetTwoFactor = () => {
    setTwoFactorRequired(false);
    setTwoFactorUserId(null);
    setUserName("");
    setSubmitError(null);
  };

  return {
    isSubmitting,
    submitError,
    errors: errors as FieldErrors<SignInFormData>,
    register,
    handleSubmit,
    onSubmit,
    loginWithGoogle,
    resetForm: reset,
    twoFactorRequired,
    twoFactorUserId,
    userName,
    resetTwoFactor,
    notConfirmed,
    resetNotConfirmed,
  };
};
